<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';
import { useQuotesStore } from 'dashboard/stores/quotes';
import { format, parseISO, isValid } from 'date-fns';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ActivityTimeline from 'dashboard/components-next/Activities/ActivityTimeline/ActivityTimeline.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const route = useRoute();
const router = useRouter();
const quotesStore = useQuotesStore();
const { t } = useI18n();

const isEditing = ref(false);
const isSaving = ref(false);
const showRejectDialog = ref(false);
const rejectReason = ref('');
const activeTab = ref('activities');
const hasError = ref(false);

const quoteId = computed(() => parseInt(route.params.quoteId));
const uiFlags = computed(() => quotesStore.getUIFlags);
const selectedQuote = computed(() => quotesStore.getSelectedQuote);

const isFetchingItem = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.updating);
const isSending = computed(() => uiFlags.value.sending);
const isAccepting = computed(() => uiFlags.value.accepting);
const isRejecting = computed(() => uiFlags.value.rejecting);
const isDeleting = computed(() => uiFlags.value.deleting);

// Local loading states for actions not tracked in store
const isDownloading = ref(false);

// Local editable copy of quote data
const quoteData = ref({});

const QUOTE_TABS_OPTIONS = [
  { key: 'ACTIVITIES', value: 'activities' },
  { key: 'TASKS', value: 'tasks' },
];

const tabs = computed(() => {
  return QUOTE_TABS_OPTIONS.map(tab => ({
    label: t(`ACTIVITIES.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return QUOTE_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('QUOTES.TITLE'),
      link: '#',
    },
  ];
  if (selectedQuote.value) {
    items.push({
      label: selectedQuote.value.title || t('QUOTES.UNTITLED'),
    });
  }
  return items;
});

const statusColorClass = computed(() => {
  if (!quoteData.value?.status) return '';
  const colors = {
    draft: 'bg-n-slate-9/10 text-n-slate-11',
    sent: 'bg-n-blue-9/10 text-n-blue-11',
    accepted: 'bg-n-teal-9/10 text-n-teal-11',
    rejected: 'bg-n-ruby-9/10 text-n-ruby-11',
    expired: 'bg-n-amber-9/10 text-n-amber-11',
  };
  return colors[quoteData.value.status] || colors.draft;
});

const statusIcon = computed(() => {
  const icons = {
    draft: 'i-lucide-file-draft',
    sent: 'i-lucide-send',
    accepted: 'i-lucide-check-circle',
    rejected: 'i-lucide-x-circle',
    expired: 'i-lucide-clock',
  };
  return icons[quoteData.value?.status] || icons.draft;
});

const statusLabel = computed(() => {
  const status = quoteData.value?.status;
  if (!status) return '';
  return t(`QUOTES.STATUS.${status.toUpperCase()}`);
});

const formattedTotal = computed(() => {
  const total = quoteData.value?.totalAmount ?? quoteData.value?.total;
  if (total === null || total === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: quoteData.value?.currency || 'USD',
    minimumFractionDigits: 2,
  }).format(total);
});

const formattedSubtotal = computed(() => {
  const subtotal = quoteData.value?.subtotal;
  if (subtotal === null || subtotal === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: quoteData.value?.currency || 'USD',
    minimumFractionDigits: 2,
  }).format(subtotal);
});

const formattedTax = computed(() => {
  const tax = quoteData.value?.taxAmount;
  if (tax === null || tax === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: quoteData.value?.currency || 'USD',
    minimumFractionDigits: 2,
  }).format(tax);
});

// Safely format date
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return '';
    return format(date, 'MMM d, yyyy');
  } catch (e) {
    return '';
  }
};

const validUntil = computed(() => {
  return formatDate(quoteData.value?.validUntil);
});

// Action button visibility based on status
const canEdit = computed(() => quoteData.value?.status === 'draft');
const canSend = computed(() => quoteData.value?.status === 'draft');
const canDelete = computed(() => quoteData.value?.status === 'draft');
const canAccept = computed(() => quoteData.value?.status === 'sent');
const canReject = computed(() => quoteData.value?.status === 'sent');

// Initialize local data from store
const initializeData = () => {
  if (selectedQuote.value && typeof selectedQuote.value === 'object') {
    quoteData.value = { ...selectedQuote.value };
  } else {
    quoteData.value = {};
  }
};

watch(() => selectedQuote.value, initializeData, { immediate: true });

const goToQuotesList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/crm/quotes`);
  }
};

const handleBreadcrumbClick = () => {
  goToQuotesList();
};

const fetchQuote = async () => {
  if (!quoteId.value) return;
  hasError.value = false;
  try {
    await quotesStore.show(quoteId.value);
  } catch (error) {
    hasError.value = true;
    console.error('Failed to fetch quote:', error);
    useAlert(t('QUOTES.FETCH_ERROR'));
  }
};

const handleUpdateQuote = async () => {
  if (!quoteData.value) return;

  isSaving.value = true;
  try {
    await quotesStore.update(quoteId.value, {
      title: quoteData.value.title,
      valid_until: quoteData.value.validUntil,
      terms_and_conditions: quoteData.value.termsAndConditions,
    });
    useAlert(t('QUOTES.UPDATE_SUCCESS'));
    isEditing.value = false;
  } catch (error) {
    useAlert(t('QUOTES.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleDeleteQuote = async () => {
  if (!confirm(t('QUOTES.DELETE_CONFIRM'))) return;

  try {
    await quotesStore.delete(quoteId.value);
    useAlert(t('QUOTES.DELETE_SUCCESS'));
    goToQuotesList();
  } catch (error) {
    useAlert(t('QUOTES.DELETE_ERROR'));
  }
};

const handleSendQuote = async (method = 'conversation') => {
  try {
    await quotesStore.sendQuote(quoteId.value, method);
    useAlert(t('QUOTES.SEND_SUCCESS'));
  } catch (error) {
    useAlert(t('QUOTES.SEND_ERROR'));
  }
};

const handleDownloadQuote = async () => {
  if (isDownloading.value) return;
  isDownloading.value = true;
  try {
    await quotesStore.download(quoteId.value);
    useAlert(t('QUOTES.DOWNLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('QUOTES.DOWNLOAD_ERROR'));
  } finally {
    isDownloading.value = false;
  }
};

const handleAcceptQuote = async () => {
  try {
    await quotesStore.accept(quoteId.value);
    useAlert(t('QUOTES.ACCEPT_SUCCESS'));
  } catch (error) {
    useAlert(t('QUOTES.ACCEPT_ERROR'));
  }
};

const openRejectDialog = () => {
  rejectReason.value = '';
  showRejectDialog.value = true;
};

const handleRejectQuote = async () => {
  try {
    await quotesStore.reject(quoteId.value, rejectReason.value);
    useAlert(t('QUOTES.REJECT_SUCCESS'));
    showRejectDialog.value = false;
    rejectReason.value = '';
  } catch (error) {
    useAlert(t('QUOTES.REJECT_ERROR'));
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const startEditing = () => {
  initializeData();
  isEditing.value = true;
};

const cancelEditing = () => {
  initializeData();
  isEditing.value = false;
};

// Format currency for line items
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: quoteData.value?.currency || 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

// Calculate line item total
const calculateLineItemTotal = (item) => {
  const quantity = item.quantity || 0;
  const unitPrice = item.unitPrice || 0;
  const taxRate = item.taxRate || 0;
  const subtotal = quantity * unitPrice;
  const tax = subtotal * (taxRate / 100);
  return subtotal + tax;
};

// Refetch when quoteId changes
watch(quoteId, () => {
  fetchQuote();
});

onMounted(() => {
  fetchQuote();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <!-- Loading State -->
    <div
      v-if="isFetchingItem"
      class="flex items-center justify-center flex-1 h-full"
    >
      <Spinner />
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-alert-circle text-n-ruby-9 size-12" />
      <p class="text-n-slate-11">{{ t('QUOTES.FETCH_ERROR') }}</p>
      <Button :label="t('QUOTES.RETRY')" @click="fetchQuote" />
    </div>

    <!-- Content -->
    <section
      v-else-if="selectedQuote"
      class="flex w-full h-full overflow-hidden justify-evenly bg-n-background"
    >
      <!-- Main Content -->
      <div
        class="flex flex-col w-full h-full transition-all duration-300 ltr:2xl:ml-56 rtl:2xl:mr-56"
      >
        <header class="sticky top-0 z-10 px-6 3xl:px-0">
          <div class="w-full mx-auto max-w-[40.625rem]">
            <div
              class="flex flex-col xs:flex-row items-start xs:items-center justify-between w-full py-7 gap-2"
            >
              <Breadcrumb
                :items="breadcrumbItems"
                @click="handleBreadcrumbClick"
              />
              <div class="flex items-center gap-2">
                <template v-if="!isEditing">
                  <!-- Always show download -->
                  <Button
                    :label="t('QUOTES.ACTIONS.DOWNLOAD')"
                    size="sm"
                    variant="ghost"
                    icon="i-lucide-download"
                    :is-loading="isDownloading"
                    :disabled="isDownloading"
                    @click="handleDownloadQuote"
                  />
                  <!-- Draft actions -->
                  <template v-if="canEdit">
                    <Button
                      :label="t('QUOTES.EDIT')"
                      size="sm"
                      slate
                      :is-loading="isUpdating"
                      :disabled="isUpdating"
                      @click="startEditing"
                    />
                  </template>
                  <!-- Sent actions -->
                  <template v-else-if="canAccept">
                    <Button
                      :label="t('QUOTES.ACTIONS.ACCEPT')"
                      size="sm"
                      teal
                      :is-loading="isAccepting"
                      :disabled="isAccepting"
                      @click="handleAcceptQuote"
                    />
                    <Button
                      :label="t('QUOTES.ACTIONS.REJECT')"
                      size="sm"
                      ruby
                      :is-loading="isRejecting"
                      :disabled="isRejecting"
                      @click="openRejectDialog"
                    />
                  </template>
                </template>
                <template v-else>
                  <Button
                    :label="t('QUOTES.CANCEL')"
                    size="sm"
                    variant="ghost"
                    slate
                    @click="cancelEditing"
                  />
                  <Button
                    :label="t('QUOTES.SAVE')"
                    size="sm"
                    :is-loading="isSaving"
                    @click="handleUpdateQuote"
                  />
                </template>
              </div>
            </div>
          </div>
        </header>

        <main class="flex-1 px-6 overflow-y-auto 3xl:px-px">
          <div class="w-full py-4 mx-auto max-w-[40.625rem]">
            <!-- Quote Header -->
            <div class="flex flex-col gap-6 pb-6">
              <!-- Title and Total Row -->
              <div class="flex flex-col gap-4">
                <!-- Read Mode Header -->
                <div v-if="!isEditing" class="flex flex-col gap-3">
                  <div class="flex items-start justify-between gap-4">
                    <div class="flex flex-col gap-2">
                      <div class="flex items-center gap-2">
                        <span class="text-sm text-n-slate-11">
                          #{{ quoteData.quoteNumber || quoteData.id }}
                        </span>
                      </div>
                      <h1 class="text-2xl font-semibold text-n-slate-12">
                        {{ quoteData.title || t('QUOTES.UNTITLED') }}
                      </h1>
                      <div
                        :class="[
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium w-fit',
                          statusColorClass,
                        ]"
                      >
                        <span :class="statusIcon" class="size-3.5" />
                        {{ statusLabel }}
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-3xl font-bold text-n-slate-12">
                        {{ formattedTotal }}
                      </div>
                      <div
                        v-if="quoteData.currency"
                        class="text-sm text-n-slate-11"
                      >
                        {{ quoteData.currency }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Edit Mode Header -->
                <div v-else class="flex flex-col gap-4">
                  <Input
                    v-model="quoteData.title"
                    :label="t('QUOTES.FORM.TITLE_LABEL')"
                    :placeholder="t('QUOTES.FORM.TITLE_PLACEHOLDER')"
                  />
                </div>
              </div>

              <!-- Key Info Grid -->
              <div
                class="grid grid-cols-2 gap-4 p-4 rounded-lg bg-n-alpha-2"
              >
                <!-- Valid Until -->
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('QUOTES.STATUS_LABEL') }}
                  </span>
                  <div v-if="!isEditing" class="text-sm text-n-slate-12">
                    {{ validUntil || '-' }}
                  </div>
                  <Input
                    v-else
                    v-model="quoteData.validUntil"
                    type="date"
                    size="sm"
                  />
                </div>

                <!-- Status -->
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ t('QUOTES.FORM.VALID_UNTIL.LABEL') }}
                  </span>
                  <div class="text-sm text-n-slate-12">
                    {{ statusLabel }}
                  </div>
                </div>
              </div>

              <!-- Relation Info Cards -->
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <!-- Company -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <div
                    class="flex items-center justify-center w-10 h-10 rounded-lg bg-n-slate-3"
                  >
                    <span class="i-lucide-building-2 text-n-slate-11 size-5" />
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('DEALS.FORM.COMPANY_LABEL') }}
                    </span>
                    <router-link
                      v-if="quoteData.company?.id"
                      :to="`/app/accounts/${route.params.accountId}/companies/${quoteData.company.id}`"
                      class="text-sm font-medium text-n-slate-12 truncate hover:text-n-brand transition-colors"
                    >
                      {{ quoteData.company.name }}
                    </router-link>
                    <span v-else class="text-sm text-n-slate-12">
                      {{ t('QUOTES.NO_COMPANY') }}
                    </span>
                  </div>
                </div>

                <!-- Contact -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <Avatar
                    :src="quoteData.contact?.avatarUrl"
                    :name="quoteData.contact?.name || ''"
                    :size="40"
                    rounded-full
                  />
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('DEALS.FORM.CONTACT_LABEL') }}
                    </span>
                    <router-link
                      v-if="quoteData.contact?.id"
                      :to="`/app/accounts/${route.params.accountId}/contacts/${quoteData.contact.id}`"
                      class="text-sm font-medium text-n-slate-12 truncate hover:text-n-brand transition-colors"
                    >
                      {{ quoteData.contact.name }}
                    </router-link>
                    <span v-else class="text-sm text-n-slate-12"> - </span>
                  </div>
                </div>

                <!-- Assigned To -->
                <div
                  class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-2"
                >
                  <Avatar
                    :src="quoteData.user?.avatarUrl"
                    :name="quoteData.user?.name || ''"
                    :size="40"
                    rounded-full
                  />
                  <div class="flex flex-col min-w-0">
                    <span class="text-xs text-n-slate-11">
                      {{ t('DEALS.FORM.ASSIGNEE_LABEL') }}
                    </span>
                    <span class="text-sm font-medium text-n-slate-12 truncate">
                      {{ quoteData.user?.name || '-' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Line Items Table -->
              <div class="flex flex-col gap-3">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('QUOTES.LINE_ITEMS.TITLE') }}
                </span>
                <div class="overflow-x-auto rounded-lg border border-n-weak">
                  <table class="w-full text-sm">
                    <thead class="bg-n-alpha-2">
                      <tr>
                        <th class="px-4 py-2 text-left text-xs font-medium text-n-slate-11">
                          {{ t('QUOTES.LINE_ITEMS.DESCRIPTION') }}
                        </th>
                        <th class="px-4 py-2 text-right text-xs font-medium text-n-slate-11 w-20">
                          {{ t('QUOTES.LINE_ITEMS.QUANTITY') }}
                        </th>
                        <th class="px-4 py-2 text-right text-xs font-medium text-n-slate-11 w-28">
                          {{ t('QUOTES.LINE_ITEMS.UNIT_PRICE') }}
                        </th>
                        <th class="px-4 py-2 text-right text-xs font-medium text-n-slate-11 w-20">
                          {{ t('QUOTES.LINE_ITEMS.TAX') }}
                        </th>
                        <th class="px-4 py-2 text-right text-xs font-medium text-n-slate-11 w-28">
                          {{ t('QUOTES.LINE_ITEMS.TOTAL') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak">
                      <tr
                        v-for="(item, index) in quoteData.lineItems || []"
                        :key="index"
                        class="hover:bg-n-alpha-1"
                      >
                        <td class="px-4 py-3 text-n-slate-12">
                          {{ item.description }}
                        </td>
                        <td class="px-4 py-3 text-right text-n-slate-12">
                          {{ item.quantity }}
                        </td>
                        <td class="px-4 py-3 text-right text-n-slate-12">
                          {{ formatCurrency(item.unitPrice) }}
                        </td>
                        <td class="px-4 py-3 text-right text-n-slate-11">
                          {{ item.taxRate ? `${item.taxRate}%` : '-' }}
                        </td>
                        <td class="px-4 py-3 text-right font-medium text-n-slate-12">
                          {{ formatCurrency(calculateLineItemTotal(item)) }}
                        </td>
                      </tr>
                      <tr v-if="!(quoteData.lineItems?.length)">
                        <td colspan="5" class="px-4 py-8 text-center text-n-slate-11">
                          {{ t('QUOTES.LINE_ITEMS.EMPTY') }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Totals Section -->
              <div class="flex justify-end">
                <div class="w-full max-w-xs space-y-2">
                  <div class="flex justify-between text-sm">
                    <span class="text-n-slate-11">{{ t('QUOTES.SUBTOTAL') }}</span>
                    <span class="text-n-slate-12">{{ formattedSubtotal }}</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-n-slate-11">{{ t('QUOTES.TAX') }}</span>
                    <span class="text-n-slate-12">{{ formattedTax }}</span>
                  </div>
                  <div class="flex justify-between text-base font-semibold pt-2 border-t border-n-weak">
                    <span class="text-n-slate-12">{{ t('QUOTES.TOTAL') }}</span>
                    <span class="text-n-slate-12">{{ formattedTotal }}</span>
                  </div>
                </div>
              </div>

              <!-- Terms and Conditions -->
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('QUOTES.FORM.TERMS_LABEL') }}
                </span>
                <p
                  v-if="!isEditing"
                  class="text-sm text-n-slate-11 whitespace-pre-wrap"
                >
                  {{ quoteData.termsAndConditions || '-' }}
                </p>
                <TextArea
                  v-else
                  v-model="quoteData.termsAndConditions"
                  rows="4"
                  :placeholder="t('QUOTES.FORM.TERMS_PLACEHOLDER')"
                />
              </div>

              <!-- Draft Action Buttons -->
              <div
                v-if="canSend && !isEditing"
                class="flex items-center gap-3 pt-4 border-t border-n-weak"
              >
                <Button
                  :label="t('QUOTES.ACTIONS.SEND')"
                  icon="i-lucide-send"
                  :is-loading="isSending"
                  :disabled="isSending"
                  @click="handleSendQuote('conversation')"
                />
                <Button
                  v-if="quoteData.contact?.email"
                  :label="t('QUOTES.ACTIONS.SEND_EMAIL')"
                  icon="i-lucide-mail"
                  variant="secondary"
                  :is-loading="isSending"
                  :disabled="isSending"
                  @click="handleSendQuote('email')"
                />
              </div>
            </div>

            <!-- Danger Zone - Delete -->
            <div
              v-if="canDelete && !isEditing"
              class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
            >
              <div class="flex flex-col gap-2">
                <h6 class="text-base font-medium text-n-slate-12">
                  {{ t('QUOTES.DELETE') }}
                </h6>
                <span class="text-sm text-n-slate-11">
                  {{ t('QUOTES.DELETE_DESCRIPTION') }}
                </span>
              </div>
              <Button
                :label="t('QUOTES.DELETE')"
                color="ruby"
                :is-loading="isDeleting"
                :disabled="isDeleting"
                @click="handleDeleteQuote"
              />
            </div>
          </div>
        </main>
      </div>

      <!-- Sidebar -->
      <div
        class="hidden lg:block overflow-y-auto justify-end min-w-52 w-full py-6 max-w-md border-l border-n-weak bg-n-solid-2"
      >
        <div class="px-6">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>

        <!-- Activities Tab -->
        <div v-if="activeTab === 'activities'" class="mt-4 h-[calc(100%-3rem)]">
          <ActivityTimeline
            activitable-type="Quote"
            :activitable-id="quoteId"
          />
        </div>

        <!-- Tasks Tab -->
        <div v-if="activeTab === 'tasks'" class="px-6 mt-4">
          <div class="flex flex-col gap-4">
            <div
              class="flex items-center justify-center py-8 text-sm text-n-slate-11"
            >
              {{ t('TASKS.EMPTY') }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Not Found State -->
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-file-text text-n-slate-9 size-12" />
      <p class="text-n-slate-11">{{ t('QUOTES.EMPTY_STATE.LIST_EMPTY_STATE_TITLE') }}</p>
      <Button :label="t('QUOTES.BACK')" @click="goToQuotesList" />
    </div>

    <!-- Reject Dialog -->
    <div
      v-if="showRejectDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black3"
      @click.self="showRejectDialog = false"
    >
      <div class="w-full max-w-md p-6 mx-4 rounded-xl bg-n-solid-1 shadow-lg">
        <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
          {{ t('QUOTES.REJECT_DIALOG.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('QUOTES.REJECT_DIALOG.DESCRIPTION') }}
        </p>
        <div class="mb-4">
          <TextArea
            v-model="rejectReason"
            rows="3"
            :placeholder="t('QUOTES.REJECT_DIALOG.REASON_PLACEHOLDER')"
          />
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('QUOTES.CANCEL')"
            variant="ghost"
            slate
            @click="showRejectDialog = false"
          />
          <Button
            :label="t('QUOTES.ACTIONS.REJECT')"
            color="ruby"
            :is-loading="isRejecting"
            :disabled="isRejecting"
            @click="handleRejectQuote"
          />
        </div>
      </div>
    </div>
  </div>
</template>
